import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import L from 'leaflet';
import { useAuth } from '../contexts/AuthContext';
import { usePillar } from '../contexts/PillarContext';
import {
  getSearchHistory,
  rerunSearch,
  deleteSearchHistory,
  deleteAllSearchHistory,
  getFavorites,
  removeFavorite,
  getLifestyleProfile,
  updateLifestyleProfile,
  synthesizeLifestyleProfile,
  resetLifestyleProfile,
  getLifestyleToggle,
  setLifestyleToggle as persistLifestyleToggle,
  getComparisons,
  deleteComparison,
  getValuations,
  deleteValuation,
  deleteAllValuations,
  getAccessToken,
  apiRequest,
} from '../services/authService';

type TabType = 'searches' | 'favorites' | 'comparisons' | 'lifestyle-edit' | 'my-places' | 'valuations' | 'projects' | 'newbuild-edit';

const VALID_TABS: TabType[] = ['searches', 'favorites', 'comparisons', 'lifestyle-edit', 'my-places', 'valuations', 'projects', 'newbuild-edit'];

export default function ProfilePage() {
  const { user, isAuthenticated, isLoading } = useAuth();
  const { isRate, isNewbuild } = usePillar();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Read tab from URL query parameter (e.g., /profile?tab=favorites)
  const tabFromUrl = searchParams.get('tab') as TabType | null;
  const defaultTab: TabType = isNewbuild ? 'projects' : isRate ? 'valuations' : 'searches';
  const initialTab = tabFromUrl && VALID_TABS.includes(tabFromUrl) ? tabFromUrl : defaultTab;
  const [activeTab, setActiveTab] = useState<TabType>(initialTab);

  // Lifestyle profile state (shown above tabs)
  const [lifestyleSummary, setLifestyleSummary] = useState<string | null>(null);
  const [lifestyleProfile, setLifestyleProfile] = useState<any>(null);
  const [lifestyleUpdatedAt, setLifestyleUpdatedAt] = useState<string | null>(null);
  const [lifestyleLoading, setLifestyleLoading] = useState(true);
  const [synthesizing, setSynthesizing] = useState(false);
  const [lifestyleMessage, setLifestyleMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);
  const [lifestyleEnabled, setLifestyleEnabled] = useState<boolean>(getLifestyleToggle());
  const [statsSearches, setStatsSearches] = useState<number>(0);
  const [statsFavorites, setStatsFavorites] = useState<number>(0);
  const [bgImage, setBgImage] = useState<any>(null);

  // Load stats for header
  useEffect(() => {
    if (isAuthenticated) {
      getSearchHistory().then(h => setStatsSearches(h.length)).catch(() => {});
      getFavorites().then(f => setStatsFavorites(f.length)).catch(() => {});
    }
  }, [isAuthenticated]);

  // Fetch profile page background image settings (pillar-aware)
  useEffect(() => {
    const endpoint = isNewbuild
      ? '/api/admin/newbuild-profile-image-settings'
      : isRate
        ? '/api/admin/rate-profile-image-settings'
        : '/api/admin/page-image-settings/profile';
    fetch(endpoint)
      .then(r => r.json())
      .then(data => {
        if (data.success && data.data?.enabled && data.data?.filename) {
          setBgImage(data.data);
        } else {
          setBgImage(null);
        }
      })
      .catch(() => {});
  }, [isRate, isNewbuild]);

  // Newbuild profile state
  const [newbuildProfile, setNewbuildProfile] = useState<any>(null);
  const [newbuildSummary, setNewbuildSummary] = useState<string | null>(null);
  const [newbuildUpdatedAt, setNewbuildUpdatedAt] = useState<string | null>(null);
  const [newbuildLoading, setNewbuildLoading] = useState(true);

  // Load newbuild profile
  useEffect(() => {
    if (isAuthenticated && isNewbuild) {
      loadNewbuildProfile();
    }
  }, [isAuthenticated, isNewbuild]);

  async function loadNewbuildProfile() {
    try {
      setNewbuildLoading(true);
      const token = getAccessToken();
      const res = await fetch('/api/newbuild-profile', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success && data.data) {
        setNewbuildProfile(data.data.profile);
        setNewbuildSummary(data.data.summary);
        setNewbuildUpdatedAt(data.data.updatedAt);
      }
    } catch {
      // No profile yet
    } finally {
      setNewbuildLoading(false);
    }
  }

  async function handleNewbuildReset() {
    if (!confirm('Möchten Sie Ihr Neubauprofil wirklich zurücksetzen?')) return;
    try {
      const token = getAccessToken();
      await fetch('/api/newbuild-profile', {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      setNewbuildProfile(null);
      setNewbuildSummary(null);
      setNewbuildUpdatedAt(null);
    } catch { /* ignore */ }
  }

  function handleLifestyleToggle() {
    const newValue = !lifestyleEnabled;
    setLifestyleEnabled(newValue);
    persistLifestyleToggle(newValue);
  }

  // Update tab when URL changes
  useEffect(() => {
    if (tabFromUrl && VALID_TABS.includes(tabFromUrl)) {
      setActiveTab(tabFromUrl);
    }
  }, [tabFromUrl]);

  // Reset to default tab when pillar changes
  useEffect(() => {
    const pillarDefault: TabType = isNewbuild ? 'projects' : isRate ? 'valuations' : 'searches';
    const rateTabs: TabType[] = ['valuations'];
    const newbuildTabs: TabType[] = ['projects', 'newbuild-edit'];
    if (isNewbuild && !newbuildTabs.includes(activeTab)) {
      setActiveTab(pillarDefault);
    } else if (isRate && !rateTabs.includes(activeTab)) {
      setActiveTab(pillarDefault);
    }
  }, [isRate, isNewbuild]);

  // Redirect if not logged in
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate('/#lab-auth');
    }
  }, [isAuthenticated, isLoading, navigate]);

  // Load lifestyle profile
  useEffect(() => {
    if (isAuthenticated) {
      loadLifestyle();
    }
  }, [isAuthenticated]);

  async function loadLifestyle() {
    try {
      setLifestyleLoading(true);
      const data = await getLifestyleProfile();
      setLifestyleProfile(data.profile);
      setLifestyleSummary(data.summary);
      setLifestyleUpdatedAt(data.updatedAt);
    } catch {
      // No profile yet – that's fine
    } finally {
      setLifestyleLoading(false);
    }
  }

  async function handleSynthesize() {
    try {
      setSynthesizing(true);
      setLifestyleMessage(null);
      const result = await synthesizeLifestyleProfile();
      setLifestyleProfile(result.profile);
      setLifestyleSummary(result.summary);
      setLifestyleUpdatedAt(new Date().toISOString());
      setLifestyleMessage({ text: `Profil aktualisiert aus ${result.searchCount} Suche(n).`, type: 'success' });
    } catch (err: any) {
      setLifestyleMessage({ text: err.message || 'Fehler bei der Aktualisierung', type: 'error' });
    } finally {
      setSynthesizing(false);
    }
  }

  async function handleReset() {
    if (!confirm('Möchten Sie Ihr Lebensstil-Profil wirklich zurücksetzen? Diese Aktion kann nicht rückgängig gemacht werden.')) return;
    try {
      setLifestyleMessage(null);
      await resetLifestyleProfile();
      setLifestyleProfile(null);
      setLifestyleSummary(null);
      setLifestyleUpdatedAt(null);
      setLifestyleMessage({ text: 'Profil zurückgesetzt.', type: 'success' });
    } catch (err: any) {
      setLifestyleMessage({ text: err.message || 'Fehler beim Zurücksetzen', type: 'error' });
    }
  }

  if (isLoading) {
    return (
      <div className="loading-page">
        <div className="spinner" />
        <p>Laden...</p>
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="profile-page">
      {/* Fixed background image (admin-configurable) */}
      {bgImage && bgImage.enabled && bgImage.filename && (
        <div
          className="profile-bg-image"
          style={{
            backgroundImage: `url(/uploads/${bgImage.filename})`,
            opacity: bgImage.opacity / 100,
            mixBlendMode: bgImage.blend_mode as any,
            filter: bgImage.blur > 0 ? `blur(${bgImage.blur}px)` : undefined,
          }}
        />
      )}

      <div className="profile-header">
        <div className="profile-avatar">
          {user.name.charAt(0).toUpperCase()}
        </div>
        <div className="profile-info">
          <h1 className="profile-name">{user.name}</h1>
          <p className="profile-email">{user.email}</p>
          <p className="profile-member-since">
            Mitglied seit {new Date(user.created_at).toLocaleDateString('de-CH', {
              year: 'numeric', month: 'long', day: 'numeric'
            })}
          </p>
        </div>
        {!isRate && !isNewbuild && (
          <div className="profile-stats">
            <div className="profile-stat">
              <span className="profile-stat-value">{statsSearches}</span>
              <span className="profile-stat-label">Suchen</span>
            </div>
            <div className="profile-stat-divider" />
            <div className="profile-stat">
              <span className="profile-stat-value">{statsFavorites}</span>
              <span className="profile-stat-label">Favoriten</span>
            </div>
          </div>
        )}
      </div>

      {/* Newbuild Profile Summary – only visible in Newbuild pillar */}
      {isNewbuild && (
        <div className="lifestyle-summary-section newbuild-summary-section">
          <div className="lifestyle-summary-header">
            <div className="lifestyle-summary-title-row">
              <h2 className="lifestyle-summary-title">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="#C75B4A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
                Mein Neubauprofil
              </h2>
            </div>
            {newbuildUpdatedAt && (
              <span className="lifestyle-updated-at">
                Aktualisiert: {new Date(newbuildUpdatedAt).toLocaleDateString('de-CH', {
                  year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                })}
              </span>
            )}
          </div>

          {newbuildLoading ? (
            <div className="lifestyle-summary-loading">
              <div className="spinner-small" /> Profil wird geladen...
            </div>
          ) : newbuildSummary ? (
            <div className="lifestyle-summary-content">
              {newbuildProfile && (
                <div className="lifestyle-quick-tags">
                  {newbuildProfile.building_type && (
                    <span className="lifestyle-tag" style={{ borderColor: 'rgba(199,91,74,0.3)', color: '#E8856F' }}>
                      {newbuildProfile.building_type === 'detached' ? 'Einfamilienhaus' : newbuildProfile.building_type === 'semi_detached' ? 'Doppelhaushälfte' : newbuildProfile.building_type === 'apartment_building' ? 'Mehrfamilienhaus' : newbuildProfile.building_type}
                    </span>
                  )}
                  {newbuildProfile.total_living_area_m2 && (
                    <span className="lifestyle-tag" style={{ borderColor: 'rgba(199,91,74,0.3)', color: '#E8856F' }}>
                      {newbuildProfile.total_living_area_m2} m²
                    </span>
                  )}
                  {newbuildProfile.bedrooms && (
                    <span className="lifestyle-tag" style={{ borderColor: 'rgba(199,91,74,0.3)', color: '#E8856F' }}>
                      {newbuildProfile.bedrooms} Schlafzimmer
                    </span>
                  )}
                  {newbuildProfile.floors && (
                    <span className="lifestyle-tag" style={{ borderColor: 'rgba(199,91,74,0.3)', color: '#E8856F' }}>
                      {newbuildProfile.floors} Stockwerke
                    </span>
                  )}
                  {newbuildProfile.roof_type && (
                    <span className="lifestyle-tag" style={{ borderColor: 'rgba(199,91,74,0.3)', color: '#E8856F' }}>
                      {newbuildProfile.roof_type === 'flat' ? 'Flachdach' : newbuildProfile.roof_type === 'gable' ? 'Satteldach' : newbuildProfile.roof_type === 'shed' ? 'Pultdach' : newbuildProfile.roof_type === 'green' ? 'Gründach' : newbuildProfile.roof_type}
                    </span>
                  )}
                  {newbuildProfile.heating_type && (
                    <span className="lifestyle-tag" style={{ borderColor: 'rgba(199,91,74,0.3)', color: '#E8856F' }}>
                      {newbuildProfile.heating_type === 'heat_pump_geothermal' ? 'Erdwärme' : newbuildProfile.heating_type === 'heat_pump_air' ? 'Luft-WP' : newbuildProfile.heating_type === 'district_heating' ? 'Fernwärme' : newbuildProfile.heating_type}
                    </span>
                  )}
                  {newbuildProfile.budget_chf && (
                    <span className="lifestyle-tag" style={{ borderColor: 'rgba(199,91,74,0.3)', color: '#E8856F' }}>
                      CHF {(newbuildProfile.budget_chf / 1000000).toFixed(1)} Mio.
                    </span>
                  )}
                  {newbuildProfile.plot_municipality && (
                    <span className="lifestyle-tag" style={{ borderColor: 'rgba(199,91,74,0.3)', color: '#E8856F' }}>
                      {newbuildProfile.plot_municipality}
                    </span>
                  )}
                </div>
              )}
              <p className="lifestyle-summary-text">{newbuildSummary}</p>
            </div>
          ) : (
            <div className="lifestyle-summary-empty">
              <p>Noch kein Neubauprofil vorhanden. Führen Sie ein Beratungsgespräch auf der <a href="/newbuild" style={{ color: '#C75B4A' }}>Newbuild-Startseite</a> durch – Ihr Profil wird automatisch erstellt.</p>
            </div>
          )}

          <div className="lifestyle-summary-actions">
            {newbuildProfile && (
              <button className="btn btn--danger btn--sm" onClick={handleNewbuildReset}>
                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg> Profil zurücksetzen
              </button>
            )}
          </div>
        </div>
      )}

      {/* Lifestyle Profile Summary – only visible in Search pillar */}
      {!isRate && !isNewbuild && <div className="lifestyle-summary-section">
        <div className="lifestyle-summary-header">
          <div className="lifestyle-summary-title-row">
            <h2 className="lifestyle-summary-title">
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="#10b981" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
              Mein Lebensstil-Profil
            </h2>
            {lifestyleProfile && (
              <div className="lifestyle-toggle-wrapper">
                <span className={`lifestyle-toggle-status ${lifestyleEnabled ? 'lifestyle-toggle-status--on' : 'lifestyle-toggle-status--off'}`}>
                  {lifestyleEnabled ? 'Aktiv' : 'Inaktiv'}
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={lifestyleEnabled}
                  className={`toggle-switch ${lifestyleEnabled ? 'toggle-switch--on' : ''}`}
                  onClick={handleLifestyleToggle}
                  title={lifestyleEnabled ? 'Lebensstil-Profil im Chat deaktivieren' : 'Lebensstil-Profil im Chat aktivieren'}
                >
                  <span className="toggle-switch-knob" />
                </button>
              </div>
            )}
          </div>
          {lifestyleUpdatedAt && (
            <span className="lifestyle-updated-at">
              Aktualisiert: {new Date(lifestyleUpdatedAt).toLocaleDateString('de-CH', {
                year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
              })}
            </span>
          )}
        </div>

        {lifestyleLoading ? (
          <div className="lifestyle-summary-loading">
            <div className="spinner-small" /> Profil wird geladen...
          </div>
        ) : lifestyleSummary ? (
          <div className="lifestyle-summary-content">
            {/* Quick-tags: visual chips extracted from structured profile */}
            {lifestyleProfile && (
              <div className="lifestyle-quick-tags">
                {lifestyleProfile.meta?.household && (
                  <span className="lifestyle-tag lifestyle-tag--meta">
                    {lifestyleProfile.meta.household === 'couple' ? 'Paar' : lifestyleProfile.meta.household === 'family' ? 'Familie' : lifestyleProfile.meta.household === 'single' ? 'Single' : lifestyleProfile.meta.household}
                  </span>
                )}
                {lifestyleProfile.hard?.max_price_chf && (
                  <span className="lifestyle-tag lifestyle-tag--hard">
                    CHF {(lifestyleProfile.hard.max_price_chf / 1000000).toFixed(1)} Mio.
                  </span>
                )}
                {lifestyleProfile.hard?.min_rooms && (
                  <span className="lifestyle-tag lifestyle-tag--hard">
                    {lifestyleProfile.hard.min_rooms}+ Zimmer
                  </span>
                )}
                {lifestyleProfile.hard?.property_type && lifestyleProfile.hard.property_type !== 'any' && (
                  <span className="lifestyle-tag lifestyle-tag--hard">
                    {lifestyleProfile.hard.property_type === 'apartment' ? 'Wohnung' : lifestyleProfile.hard.property_type === 'house' ? 'Haus' : lifestyleProfile.hard.property_type}
                  </span>
                )}
                {lifestyleProfile.hard?.transaction_type && lifestyleProfile.hard.transaction_type !== 'any' && (
                  <span className="lifestyle-tag lifestyle-tag--hard">
                    {lifestyleProfile.hard.transaction_type === 'buy' ? 'Kaufen' : 'Mieten'}
                  </span>
                )}
                {lifestyleProfile.hard?.preferred_canton && (
                  <span className="lifestyle-tag lifestyle-tag--location">
                    {lifestyleProfile.hard.preferred_canton}
                  </span>
                )}
                {lifestyleProfile.hard?.preferred_city && (
                  <span className="lifestyle-tag lifestyle-tag--location">
                    {lifestyleProfile.hard.preferred_city}
                  </span>
                )}
                {lifestyleProfile.meta?.commute_type && (
                  <span className="lifestyle-tag lifestyle-tag--meta">
                    {lifestyleProfile.meta.commute_type === 'car' ? 'Auto' : lifestyleProfile.meta.commute_type === 'pt' ? 'ÖV' : lifestyleProfile.meta.commute_type === 'bike' ? 'Velo' : 'Mixed'}-Pendel
                  </span>
                )}
                {lifestyleProfile.hard?.max_commute_minutes && (
                  <span className="lifestyle-tag lifestyle-tag--hard">
                    {lifestyleProfile.hard.max_commute_minutes} Min. Pendel
                  </span>
                )}
              </div>
            )}
            <p className="lifestyle-summary-text">{lifestyleSummary}</p>
          </div>
        ) : (
          <div className="lifestyle-summary-empty">
            <p>Noch kein Lebensstil-Profil vorhanden. Führen Sie eine Immobiliensuche durch – Ihr Profil wird automatisch erstellt und mit jeder Suche genauer.</p>
          </div>
        )}

        {lifestyleProfile && !lifestyleEnabled && (
          <div className="lifestyle-toggle-hint">
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
            Ihr Lebensstil-Profil ist deaktiviert. Bei jeder neuen Suche werden alle Fragen von Grund auf gestellt, ohne Ihre gespeicherten Präferenzen zu berücksichtigen. Aktivieren Sie das Profil, um kürzere und personalisierte Gespräche zu erhalten.
          </div>
        )}

        {lifestyleMessage && (
          <div className={`lifestyle-message lifestyle-message--${lifestyleMessage.type}`}>
            {lifestyleMessage.text}
          </div>
        )}

        <div className="lifestyle-summary-actions">
          <button
            className="btn btn--secondary btn--sm"
            onClick={handleSynthesize}
            disabled={synthesizing}
          >
            {synthesizing ? (
              <><span className="spinner-small" /> Wird aktualisiert...</>
            ) : (
              <><svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg> Profil aktualisieren</>
            )}
          </button>
          {lifestyleProfile && (
            <button
              className="btn btn--danger btn--sm"
              onClick={handleReset}
            >
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg> Profil zurücksetzen
            </button>
          )}
        </div>
      </div>}

      <div className="profile-tabs">
        {isNewbuild ? (
          <>
            {/* Newbuild pillar tabs */}
            <button
              className={`profile-tab ${activeTab === 'projects' ? 'profile-tab--active' : ''}`}
              onClick={() => setActiveTab('projects')}
            >
              <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="#C75B4A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg> Meine Neubauprojekte
            </button>
            <button
              className={`profile-tab ${activeTab === 'newbuild-edit' ? 'profile-tab--active' : ''}`}
              onClick={() => setActiveTab('newbuild-edit')}
            >
              <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="#E8856F" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg> Neubauprofil bearbeiten
            </button>
          </>
        ) : isRate ? (
          <>
            {/* Rate pillar tabs: Meine Analysen */}
            <button
              className={`profile-tab ${activeTab === 'valuations' ? 'profile-tab--active' : ''}`}
              onClick={() => setActiveTab('valuations')}
            >
              <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="#00CED1" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg> Meine Analysen
            </button>
          </>
        ) : (
          <>
            {/* Search pillar tabs: all except Meine Analysen */}
            <button
              className={`profile-tab ${activeTab === 'searches' ? 'profile-tab--active' : ''}`}
              onClick={() => setActiveTab('searches')}
            >
              <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="#6366f1" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg> Meine Suchen
            </button>
            <button
              className={`profile-tab ${activeTab === 'favorites' ? 'profile-tab--active' : ''}`}
              onClick={() => setActiveTab('favorites')}
            >
              <svg viewBox="0 0 24 24" width="15" height="15" fill="#ef4444" stroke="#ef4444" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/></svg> Favoriten
            </button>
            <button
              className={`profile-tab ${activeTab === 'comparisons' ? 'profile-tab--active' : ''}`}
              onClick={() => setActiveTab('comparisons')}
            >
              <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="#8b5cf6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/><line x1="3" y1="20" x2="21" y2="20"/></svg> Objekte vergleichen
            </button>
            <button
              className={`profile-tab ${activeTab === 'lifestyle-edit' ? 'profile-tab--active' : ''}`}
              onClick={() => setActiveTab('lifestyle-edit')}
            >
              <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="#10b981" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg> Lebensstil bearbeiten
            </button>
            <button
              className={`profile-tab ${activeTab === 'my-places' ? 'profile-tab--active' : ''}`}
              onClick={() => setActiveTab('my-places')}
            >
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="#B8860B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg> Meine besonderen Orte
            </button>
          </>
        )}
      </div>

      <div className="profile-content">
        {activeTab === 'searches' && <SearchHistoryTab />}
        {activeTab === 'favorites' && <FavoritesTab />}
        {activeTab === 'comparisons' && <ComparisonsTab />}
        {activeTab === 'lifestyle-edit' && (
          <LifestyleEditTab
            profile={lifestyleProfile}
            onSave={(updated) => {
              setLifestyleProfile(updated.profile);
              setLifestyleSummary(updated.summary);
              setLifestyleUpdatedAt(new Date().toISOString());
            }}
          />
        )}
        {activeTab === 'my-places' && (
          <MyPlacesTab
            profile={lifestyleProfile}
            onSave={(updated) => {
              setLifestyleProfile(updated.profile);
              setLifestyleSummary(updated.summary);
              setLifestyleUpdatedAt(new Date().toISOString());
            }}
          />
        )}
        {activeTab === 'valuations' && <ValuationsTab />}
        {activeTab === 'projects' && <NewbuildProjectsTab />}
        {activeTab === 'newbuild-edit' && (
          <NewbuildEditTab
            profile={newbuildProfile}
            onSave={(updated) => {
              setNewbuildProfile(updated.profile);
              setNewbuildSummary(updated.summary);
              setNewbuildUpdatedAt(new Date().toISOString());
            }}
          />
        )}
        {/* Password change moved to Lab Profile page */}
      </div>
    </div>
  );
}

// ============================================
// Mini Map Preview (non-interactive)
// ============================================

function MiniMapPreview({ results, onClick }: { results: any[]; onClick: () => void }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);

  const initMap = useCallback((node: HTMLDivElement | null) => {
    if (!node || mapRef.current) return;
    containerRef.current = node;

    // Extract coordinates from results
    const coords = (results || [])
      .filter((r: any) => r.latitude && r.longitude)
      .map((r: any) => [r.latitude, r.longitude] as [number, number]);

    if (coords.length === 0) return;

    // Calculate bounds
    const bounds = L.latLngBounds(coords.map(c => L.latLng(c[0], c[1])));
    const center = bounds.getCenter();

    const map = L.map(node, {
      center: [center.lat, center.lng],
      zoom: 10,
      zoomControl: false,
      attributionControl: false,
      dragging: false,
      scrollWheelZoom: false,
      doubleClickZoom: false,
      touchZoom: false,
      boxZoom: false,
      keyboard: false,
    });

    L.tileLayer('https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', {
      maxZoom: 18,
    }).addTo(map);

    // Fit to bounds with padding
    if (coords.length > 1) {
      map.fitBounds(bounds, { padding: [15, 15], maxZoom: 13 });
    }

    // Add small dot markers
    coords.forEach(([lat, lng]) => {
      L.circleMarker([lat, lng], {
        radius: 3,
        fillColor: '#6366f1',
        color: '#818cf8',
        weight: 1,
        opacity: 0.9,
        fillOpacity: 0.8,
      }).addTo(map);
    });

    mapRef.current = map;
  }, [results]);

  useEffect(() => {
    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, []);

  const hasCoords = (results || []).some((r: any) => r.latitude && r.longitude);

  if (!hasCoords) {
    return (
      <div className="search-card-map-empty" onClick={onClick} title="Auf aktuellem Markt anzeigen">
        <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" opacity="0.4">
          <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"/>
          <line x1="8" y1="2" x2="8" y2="18"/>
          <line x1="16" y1="6" x2="16" y2="22"/>
        </svg>
      </div>
    );
  }

  return (
    <div className="search-card-map-preview" onClick={onClick} title="Auf aktuellem Markt anzeigen">
      <div ref={initMap} style={{ width: '100%', height: '100%' }} />
      <div className="search-card-map-overlay">
        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="5 3 19 12 5 21 5 3"/></svg>
        <span className="search-card-map-overlay-text">Aktualisieren</span>
      </div>
    </div>
  );
}

// ============================================
// Search History Tab
// ============================================

function SearchHistoryTab() {
  const [searches, setSearches] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [rerunningId, setRerunningId] = useState<number | null>(null);
  const [expandedInfoIds, setExpandedInfoIds] = useState<Set<number>>(new Set());
  const [expandedChatIds, setExpandedChatIds] = useState<Set<number>>(new Set());
  const navigate = useNavigate();

  function toggleInfo(id: number) {
    setExpandedInfoIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }
  function toggleChat(id: number) {
    setExpandedChatIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  useEffect(() => {
    loadSearches();
  }, []);

  async function loadSearches() {
    try {
      setLoading(true);
      const data = await getSearchHistory();
      setSearches(data);
    } catch (err: any) {
      setError(err.message || 'Fehler beim Laden der Suchen');
    } finally {
      setLoading(false);
    }
  }

  async function handleRerun(searchId: number, profile: any) {
    try {
      setRerunningId(searchId);
      const result = await rerunSearch(searchId);
      // Store results in session and navigate to results page
      sessionStorage.setItem('searchResults', JSON.stringify({
        topMatches: result.topMatches,
        nearMisses: result.nearMisses,
        stats: {
          total: result.topMatches.length + result.nearMisses.length + result.failCount,
          matched: result.topMatches.length,
          nearMiss: result.nearMisses.length,
          excluded: result.failCount,
        },
        commuteOverlap: result.commuteOverlap || null,
      }));
      sessionStorage.setItem('searchProfile', JSON.stringify(profile));
      navigate('/results');
    } catch (err: any) {
      setError(err.message || 'Fehler beim erneuten Ausführen der Suche');
    } finally {
      setRerunningId(null);
    }
  }

  async function handleDelete(searchId: number) {
    if (!confirm('Diese Suche wirklich löschen?')) return;
    try {
      await deleteSearchHistory(searchId);
      setSearches(prev => prev.filter(s => s.id !== searchId));
    } catch (err: any) {
      setError(err.message || 'Fehler beim Löschen');
    }
  }

  async function handleDeleteAll() {
    if (!confirm(`Alle ${searches.length} Suchen unwiderruflich löschen?`)) return;
    try {
      await deleteAllSearchHistory();
      setSearches([]);
    } catch (err: any) {
      setError(err.message || 'Fehler beim Löschen aller Suchen');
    }
  }

  if (loading) {
    return (
      <div className="profile-tab-loading">
        <div className="spinner" />
        <p>Suchen werden geladen...</p>
      </div>
    );
  }

  if (error) {
    return <div className="profile-error">{error}</div>;
  }

  if (searches.length === 0) {
    return (
      <div className="profile-empty">
        <div className="profile-empty-icon"><svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg></div>
        <h3>Noch keine Suchen gespeichert</h3>
        <p>Starten Sie eine neue Immobiliensuche und speichern Sie die Ergebnisse.</p>
        <button className="btn btn--primary" onClick={() => navigate('/')}>
          Neue Suche starten
        </button>
      </div>
    );
  }

  return (
    <div className="search-history-list">
      <div className="search-history-toolbar">
        <button className="btn btn--primary btn--sm" onClick={() => navigate('/')}>
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          Neue Suchanfrage starten
        </button>
        <span className="search-history-count">{searches.length} gespeicherte Suche{searches.length !== 1 ? 'n' : ''}</span>
        <button className="btn btn--danger-outline btn--sm" onClick={handleDeleteAll}>
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
          </svg>
          Alle Suchergebnisse löschen
        </button>
      </div>
      {searches.map((search) => {
        const profile = search.profile;
        const hard = profile?.hard || {};
        const canton = hard.preferred_canton || '–';
        const city = hard.preferred_city || '–';
        const maxPrice = hard.max_price_chf
          ? `CHF ${Number(hard.max_price_chf).toLocaleString('de-CH')}`
          : '–';
        const rooms = hard.min_rooms ? `${hard.min_rooms}+ Zi.` : '–';
        const txType = hard.transaction_type === 'buy' ? 'Kaufen' : hard.transaction_type === 'rent' ? 'Mieten' : 'Alle';

        // Build extended criteria for info panel
        const soft = profile?.soft || {};
        const places = profile?.places || {};
        const meta = profile?.meta || {};
        const fp = hard.feature_preferences || {};
        const isInfoOpen = expandedInfoIds.has(search.id);
        const isChatExpanded = expandedChatIds.has(search.id);
        const chatMsgs = (search.chatMessages || []).filter((m: any) => m.role === 'user');

        return (
          <div key={search.id} className="search-history-card">
            <MiniMapPreview
              results={search.results || []}
              onClick={() => handleRerun(search.id, profile)}
            />
            <div className="search-card-info">
              <div className="search-history-header">
                <div className="search-history-title-row">
                  <h3 className="search-history-title">{search.profileName || 'Suche'}</h3>
                  <span className="search-history-badge">{search.resultCount || 0} Ergebnisse</span>
                  <button
                    className={`search-info-toggle ${isInfoOpen ? 'search-info-toggle--active' : ''}`}
                    onClick={() => toggleInfo(search.id)}
                    title="Suchdetails anzeigen"
                  >
                    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
                  </button>
                </div>
                <p className="search-history-date">
                  {new Date(search.createdAt).toLocaleDateString('de-CH', {
                    year: 'numeric', month: 'short', day: 'numeric',
                    hour: '2-digit', minute: '2-digit'
                  })}
                  {search.aiProvider && (
                    <span className="search-history-provider"> · {search.aiProvider}</span>
                  )}
                </p>
              </div>

              <div className="search-history-criteria">
                <div className="search-criteria-item">
                  <span className="criteria-label">Region</span>
                  <span className="criteria-value">{canton}{city !== '–' ? ` / ${city}` : ''}</span>
                </div>
                <div className="search-criteria-item">
                  <span className="criteria-label">Budget</span>
                  <span className="criteria-value">{maxPrice}</span>
                </div>
                <div className="search-criteria-item">
                  <span className="criteria-label">Zimmer</span>
                  <span className="criteria-value">{rooms}</span>
                </div>
                <div className="search-criteria-item">
                  <span className="criteria-label">Typ</span>
                  <span className="criteria-value">{txType}</span>
                </div>
              </div>

              {/* Expandable compact info panel */}
              {isInfoOpen && (() => {
                const hasExtras = hard.min_living_area_m2 || hard.max_building_age_years || hard.max_commute_minutes || hard.max_commute_km || places.work_addresses?.length > 0 || meta.work_pattern || meta.commute_type;
                const activeFeatures = Object.entries(fp).filter(([, v]) => v === 'ja' || v === 'nein');
                const activeSoft = Object.entries(soft).filter(([, v]) => v && v !== 3);
                const chatPreviewCount = 2;
                const hasMoreChat = chatMsgs.length > chatPreviewCount;

                return (
                  <div className="search-info-panel search-info-panel--compact">
                    {/* Chat summary */}
                    {chatMsgs.length > 0 && (
                      <div className="search-info-section">
                        <span className="search-info-inline-title">Chat:</span>
                        <div className="search-info-chat search-info-chat--compact">
                          {chatMsgs.slice(0, isChatExpanded ? chatMsgs.length : chatPreviewCount).map((msg: any, i: number) => (
                            <div key={i} className="search-info-chat-msg search-info-chat-msg--compact">
                              <span className="search-info-chat-text">{typeof msg.content === 'string' ? (msg.content.length > 120 ? msg.content.substring(0, 120) + '...' : msg.content) : ''}</span>
                            </div>
                          ))}
                        </div>
                        {hasMoreChat && (
                          <button className="search-info-more-btn" onClick={(e) => { e.stopPropagation(); toggleChat(search.id); }}>
                            {isChatExpanded ? '▲ weniger' : `▼ +${chatMsgs.length - chatPreviewCount} weitere`}
                          </button>
                        )}
                      </div>
                    )}

                    {/* Extra criteria (only those NOT already shown on card) */}
                    {hasExtras && (
                      <div className="search-info-section">
                        <span className="search-info-inline-title">Details:</span>
                        <div className="search-info-tags">
                          {hard.min_living_area_m2 && <span className="search-info-tag">Min. {hard.min_living_area_m2} m²</span>}
                          {hard.max_building_age_years && <span className="search-info-tag">Max. {hard.max_building_age_years}J. alt</span>}
                          {hard.max_commute_minutes && <span className="search-info-tag">Pendeln ≤{hard.max_commute_minutes} Min.</span>}
                          {hard.max_commute_km && <span className="search-info-tag">≤{hard.max_commute_km} km</span>}
                          {places.work_addresses?.length > 0 && <span className="search-info-tag">Arbeit: {places.work_addresses.join(', ')}</span>}
                          {meta.work_pattern && <span className="search-info-tag">{meta.work_pattern === 'office' ? 'Büro' : meta.work_pattern === 'hybrid' ? 'Hybrid' : 'Remote'}</span>}
                          {meta.commute_type && <span className="search-info-tag">{meta.commute_type === 'pt' ? 'ÖV' : meta.commute_type === 'car' ? 'Auto' : 'Velo'}</span>}
                        </div>
                      </div>
                    )}

                    {/* Features + Priorities in one compact row */}
                    {(activeFeatures.length > 0 || activeSoft.length > 0) && (
                      <div className="search-info-section">
                        {activeFeatures.length > 0 && (
                          <div className="search-info-features search-info-features--compact">
                            {activeFeatures.map(([key, val]) => (
                              <span key={key} className={`search-info-feature search-info-feature--mini ${val === 'ja' ? 'search-info-feature--yes' : 'search-info-feature--no'}`}>
                                {key === 'parking' ? 'Parkpl.' : key === 'balkon' ? 'Balkon' : key === 'garten' ? 'Garten' : key === 'lift' ? 'Lift' : key === 'minergie' ? 'Minergie' : key === 'neubau' ? 'Neubau' : key === 'altbau' ? 'Altbau' : key === 'seesicht' ? 'Seesicht' : key === 'bergsicht' ? 'Bergsicht' : key}
                                {val === 'ja' ? ' ✓' : ' ✗'}
                              </span>
                            ))}
                          </div>
                        )}
                        {activeSoft.length > 0 && (
                          <div className="search-info-priorities">
                            {activeSoft.map(([key, val]) => (
                              <span key={key} className="search-info-priority">
                                {key === 'noise' ? 'Ruhe' : key === 'green' ? 'Natur' : key === 'pt' ? 'ÖV' : key === 'schools' ? 'Schulen' : key === 'tax' ? 'Steuern' : key === 'culture' ? 'Kultur' : key === 'oldtown' ? 'Altstadt' : key}
                                <span className="search-info-dots">{'●'.repeat(val as number)}{'○'.repeat(5 - (val as number))}</span>
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })()}

              <div className="search-history-actions">
                <button
                  className="btn btn--primary btn--sm"
                  onClick={() => handleRerun(search.id, profile)}
                  disabled={rerunningId === search.id}
                >
                  {rerunningId === search.id ? (
                    <>
                      <span className="spinner-small" /> Suche läuft...
                    </>
                  ) : (
                    <><svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"/><line x1="8" y1="2" x2="8" y2="18"/><line x1="16" y1="6" x2="16" y2="22"/></svg> Auf Karte anzeigen</>
                  )}
                </button>
                <button
                  className="btn btn--danger btn--sm"
                  onClick={() => handleDelete(search.id)}
                >
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg> Löschen
                </button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ============================================
// Favorites Tab
// ============================================

function FavoritesTab() {
  const [favorites, setFavorites] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    loadFavorites();
  }, []);

  async function loadFavorites() {
    try {
      setLoading(true);
      const data = await getFavorites();
      setFavorites(data);
    } catch (err: any) {
      setError(err.message || 'Fehler beim Laden der Favoriten');
    } finally {
      setLoading(false);
    }
  }

  async function handleRemoveFavorite(propertyId: number) {
    try {
      await removeFavorite(propertyId);
      setFavorites(prev => prev.filter(f => f.property_id !== propertyId));
    } catch (err: any) {
      setError(err.message || 'Fehler beim Entfernen');
    }
  }

  if (loading) {
    return (
      <div className="profile-tab-loading">
        <div className="spinner" />
        <p>Favoriten werden geladen...</p>
      </div>
    );
  }

  if (error) {
    return <div className="profile-error">{error}</div>;
  }

  if (favorites.length === 0) {
    return (
      <div className="profile-empty">
        <div className="profile-empty-icon"><svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/></svg></div>
        <h3>Noch keine Favoriten</h3>
        <p>Markieren Sie Immobilien als Favoriten, um sie hier wiederzufinden.</p>
        <button className="btn btn--primary" onClick={() => navigate('/')}>
          Immobilien suchen
        </button>
      </div>
    );
  }

  return (
    <div className="favorites-grid">
      {favorites.map((fav) => {
        const p = fav.property;
        if (!p) return null;

        const price = p.price_chf
          ? `CHF ${Number(p.price_chf).toLocaleString('de-CH')}`
          : p.rent_gross_chf
          ? `CHF ${Number(p.rent_gross_chf).toLocaleString('de-CH')}/Mt.`
          : 'Preis auf Anfrage';

        return (
          <div key={fav.id} className="favorite-card">
            <div className="favorite-card-header">
              <h3
                className="favorite-title"
                onClick={() => navigate(`/listing/${p.id}`)}
              >
                {p.title || 'Immobilie'}
              </h3>
              <button
                className="favorite-heart favorite-heart--active"
                onClick={() => handleRemoveFavorite(p.id)}
                title="Aus Favoriten entfernen"
              >
                ❤️
              </button>
            </div>

            <p className="favorite-location">
              {[p.street, `${p.zip_code || ''} ${p.city || ''}`.trim(), p.canton]
                .filter(Boolean)
                .join(', ')}
            </p>

            <div className="favorite-details">
              <span className="favorite-detail">{price}</span>
              {p.rooms && <span className="favorite-detail">{p.rooms} Zi.</span>}
              {p.living_space_m2 && <span className="favorite-detail">{p.living_space_m2} m²</span>}
              {p.property_type && p.property_type !== 'other' && (
                <span className="favorite-detail favorite-type">{p.property_type}</span>
              )}
            </div>

            <div className="favorite-meta">
              <span className="favorite-status">
                {p.is_active ? '✅ Aktiv' : '❌ Inaktiv'}
              </span>
              <span className="favorite-date">
                Gespeichert: {new Date(fav.created_at).toLocaleDateString('de-CH')}
              </span>
            </div>

            <button
              className="btn btn--secondary btn--sm btn--full"
              onClick={() => navigate(`/listing/${p.id}`)}
            >
              Details ansehen
            </button>
          </div>
        );
      })}
    </div>
  );
}

// ============================================
// Comparisons Tab
// ============================================

function ComparisonsTab() {
  const [comparisons, setComparisons] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  useEffect(() => {
    loadComparisons();
  }, []);

  async function loadComparisons() {
    try {
      setLoading(true);
      const data = await getComparisons();
      setComparisons(data);
    } catch (err) {
      console.error('Failed to load comparisons:', err);
    } finally {
      setLoading(false);
    }
  }

  async function handleDelete(id: number) {
    if (!confirm('Diesen Vergleich löschen?')) return;
    try {
      await deleteComparison(id);
      setComparisons(prev => prev.filter(c => c.id !== id));
    } catch (err) {
      console.error('Delete comparison failed:', err);
    }
  }

  /** Markdown to HTML converter – tight, gap-free rendering */
  function renderMarkdown(md: string): string {
    let html = md.replace(/\r\n/g, '\n');

    // Pre-process: move orphan colons inside bold markers  **text**: → **text:**
    html = html.replace(/\*\*([^*]+?)\*\*\s*:/g, '**$1:**');
    // Pre-process: remove blank lines between #### headers and their content
    html = html.replace(/(^#{1,4} .+)\n\n(?=- )/gm, '$1\n');

    // Tables
    html = html.replace(/(?:^\|.+\|$\n?)+/gm, (block) => {
      const rows = block.trim().split('\n').filter(r => r.trim());
      const htmlRows = rows
        .filter(row => !/^[\s|\-:]+$/.test(row)) // skip separator rows like |---|---|---|
        .map(row => {
          const cells = row.split('|').filter(c => c !== '').map(c => c.trim());
          return '<tr>' + cells.map(c => `<td>${c}</td>`).join('') + '</tr>';
        })
        .join('');
      return `<table class="comparison-table">${htmlRows}</table>\n`;
    });
    html = html
      // Headers (order matters: longest prefix first)
      .replace(/^#### (.+)$/gm, '<h5>$1</h5>')
      .replace(/^### (.+)$/gm, '<h4>$1</h4>')
      .replace(/^## (.+)$/gm, '<h3>$1</h3>')
      .replace(/^# (.+)$/gm, '<h2>$1</h2>')
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/(?<!\w)\*([^*]+?)\*(?!\w)/g, '<em>$1</em>')
      .replace(/^- (.+)$/gm, '<li>$1</li>')
      .replace(/((?:<li>.*<\/li>\n?)+)/g, '<ul>$1</ul>')
      .replace(/^---+$/gm, '<hr />')
      .replace(/\n{3,}/g, '\n\n')
      .replace(/\n\n/g, '</p><p>')
      .replace(/\n/g, '<br />')
      .replace(/^/, '<p>')
      .replace(/$/, '</p>');
    html = html
      .replace(/<p>\s*<\/p>/g, '')
      .replace(/<p><br \/>/g, '<p>')
      .replace(/<br \/><\/p>/g, '</p>')
      .replace(/<p>(<h[2345]>)/g, '$1')
      .replace(/(<\/h[2345]>)<\/p>/g, '$1')
      .replace(/<p>(<ul>)/g, '$1')
      .replace(/(<\/ul>)<\/p>/g, '$1')
      .replace(/<p>(<table)/g, '$1')
      .replace(/(<\/table>)<\/p>/g, '$1')
      .replace(/<p>(<hr \/>)/g, '$1')
      .replace(/(<hr \/>)<\/p>/g, '$1')
      .replace(/(<\/h[2345]>)<br \/>/g, '$1')
      .replace(/<br \/>(<h[2345]>)/g, '$1')
      .replace(/(<\/ul>)<br \/>/g, '$1')
      .replace(/<br \/>(<ul>)/g, '$1')
      .replace(/(<\/table>)<br \/>/g, '$1')
      .replace(/<br \/>(<table)/g, '$1')
      .replace(/(<hr \/>)<br \/>/g, '$1')
      .replace(/<br \/>(<hr \/>)/g, '$1')
      // Collapse gap between bold label and following content
      .replace(/<\/strong><\/p>\s*<p>/g, '</strong><br />')
      .replace(/<\/strong><br \/><br \/>/g, '</strong><br />')
      .replace(/<br \/>\s*(<ul>)/g, '$1')
      .replace(/<br \/>\s*(<li>)/g, '$1')
      .replace(/(<\/ul>)\s*<br \/>/g, '$1')
      // Merge adjacent <ul> blocks separated by gaps
      .replace(/<\/ul>\s*<p>\s*<\/p>\s*<ul>/g, '')
      .replace(/<\/ul>\s*<br \/>\s*<ul>/g, '')
      .replace(/<\/ul>\s*<\/p>\s*<p>\s*<ul>/g, '')
      .replace(/<\/ul>\s*<ul>/g, '')
      .replace(/<\/li>\s*<br \/>\s*<li>/g, '</li><li>')
      // Fix orphan colons: </strong><br />: or </strong>\n: → </strong>:
      .replace(/<\/strong>\s*<br \/>\s*:/g, '</strong>:')
      .replace(/<\/strong>\s*:\s*<br \/>/g, '</strong>: ')
      // Remove lone ":" on its own line (orphan from AI formatting)
      .replace(/<br \/>\s*:\s*<br \/>/g, '<br />')
      .replace(/<li>\s*:\s*<\/li>/g, '')
      .replace(/<p>\s*:\s*<\/p>/g, '');
    return html;
  }

  if (loading) {
    return <div className="loading-spinner"><div className="spinner" /> Vergleiche werden geladen...</div>;
  }

  if (comparisons.length === 0) {
    return (
      <div className="profile-empty-state">
        <svg viewBox="0 0 24 24" width="48" height="48" fill="none" stroke="#8b5cf6" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.5 }}>
          <line x1="18" y1="20" x2="18" y2="10" />
          <line x1="12" y1="20" x2="12" y2="4" />
          <line x1="6" y1="20" x2="6" y2="14" />
          <line x1="3" y1="20" x2="21" y2="20" />
        </svg>
        <h3>Noch keine Vergleiche</h3>
        <p>Wählen Sie auf der Ergebnisseite 2-3 Immobilien zum Vergleichen aus.</p>
      </div>
    );
  }

  return (
    <div className="comparisons-list">
      {comparisons.map(comp => {
        const isExpanded = expandedId === comp.id;
        const date = new Date(comp.createdAt).toLocaleDateString('de-CH', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        });

        return (
          <div key={comp.id} className={`comparison-card glass-element ${isExpanded ? 'comparison-card--expanded' : ''}`}>
            <div className="comparison-card-header" onClick={() => setExpandedId(isExpanded ? null : comp.id)}>
              <div className="comparison-card-info">
                <div className="comparison-card-title">
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="#8b5cf6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="20" x2="18" y2="10" />
                    <line x1="12" y1="20" x2="12" y2="4" />
                    <line x1="6" y1="20" x2="6" y2="14" />
                    <line x1="3" y1="20" x2="21" y2="20" />
                  </svg>
                  <strong>{comp.propertyTitles?.join(' vs. ') || `Vergleich #${comp.id}`}</strong>
                </div>
                <span className="comparison-card-date">{date}</span>
              </div>
              <div className="comparison-card-actions">
                <button
                  className="btn btn--danger btn--sm"
                  onClick={(e) => { e.stopPropagation(); handleDelete(comp.id); }}
                  title="Vergleich löschen"
                >
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                  </svg>
                </button>
                <span className={`comparison-expand-icon ${isExpanded ? 'comparison-expand-icon--open' : ''}`}>&#9660;</span>
              </div>
            </div>
            {isExpanded && (
              <div className="comparison-card-body">
                {comp.propertyIds?.length > 0 && (
                  <div className="comparison-property-links">
                    {comp.propertyIds.map((pid: number, idx: number) => (
                      <a
                        key={pid}
                        className="comparison-property-link"
                        href={`/listing/${pid}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                        <strong>Objekt {String.fromCharCode(65 + idx)}:</strong> {comp.propertyTitles?.[idx] || `Objekt ${pid}`}
                      </a>
                    ))}
                  </div>
                )}
                <div
                  className="comparison-result"
                  dangerouslySetInnerHTML={{ __html: renderMarkdown(comp.comparisonText) }}
                />
                <button
                  className="comparison-download-btn"
                  onClick={async (e) => {
                    e.stopPropagation();
                    const title = comp.propertyTitles?.join(' vs. ') || `Vergleich #${comp.id}`;
                    const objectLabels = comp.propertyTitles?.map((t: string, i: number) => `Objekt ${String.fromCharCode(65 + i)}: ${t}`).join('\n') || '';
                    // Build a clean HTML document for PDF
                    const pdfContainer = document.createElement('div');
                    pdfContainer.style.cssText = 'padding:24px;font-family:system-ui,-apple-system,sans-serif;color:#1e293b;font-size:13px;line-height:1.6;max-width:700px;';
                    pdfContainer.innerHTML = `
                      <style>
                        * { color: #1e293b !important; }
                        h1 { color: #8b5cf6 !important; }
                        h2 { font-size: 15px; margin: 18px 0 6px; border-bottom: 1px solid #e2e8f0; padding-bottom: 4px; }
                        h3 { font-size: 14px; margin: 14px 0 4px; color: #6d28d9 !important; }
                        h4 { font-size: 13px; margin: 10px 0 3px; color: #7c3aed !important; }
                        h5 { font-size: 13px; margin: 8px 0 2px; color: #6d28d9 !important; }
                        strong { color: #0f172a !important; }
                        table { width: 100%; border-collapse: collapse; margin: 8px 0 12px; font-size: 12px; }
                        td, th { padding: 6px 10px; border: 1px solid #cbd5e1; color: #1e293b !important; }
                        tr:first-child td { background: #f1f5f9; font-weight: 600; }
                        ul { padding-left: 18px; margin: 4px 0 8px; }
                        li { margin-bottom: 2px; }
                        hr { border: none; border-top: 1px solid #e2e8f0; margin: 12px 0; }
                        p { margin: 0 0 6px; }
                      </style>
                      <h1 style="font-size:18px;margin:0 0 4px;">SurrealEstateSearch – Vergleich</h1>
                      <p style="color:#64748b !important;font-size:11px;margin:0 0 16px;">${new Date(comp.createdAt).toLocaleDateString('de-CH')} · ${new Date(comp.createdAt).toLocaleTimeString('de-CH', { hour: '2-digit', minute: '2-digit' })}</p>
                      <div style="margin-bottom:16px;padding:10px 14px;background:#f1f5f9;border-radius:8px;">
                        ${(comp.propertyTitles || []).map((t: string, i: number) => `<div style="margin:4px 0;"><strong style="color:#8b5cf6 !important;">Objekt ${String.fromCharCode(65 + i)}:</strong> ${t}</div>`).join('')}
                      </div>
                      <div>${renderMarkdown(comp.comparisonText)}</div>
                    `;
                    const html2pdf = (await import('html2pdf.js')).default;
                    html2pdf().set({
                      margin: [10, 10, 10, 10],
                      filename: `Vergleich_${comp.id}_${new Date(comp.createdAt).toISOString().slice(0, 10)}.pdf`,
                      image: { type: 'jpeg', quality: 0.98 },
                      html2canvas: { scale: 2, useCORS: true },
                      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
                    }).from(pdfContainer).save();
                  }}
                >
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="7 10 12 15 17 10" />
                    <line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                  Als PDF herunterladen
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ============================================
// Lifestyle Edit Tab
// ============================================

const HOUSEHOLD_OPTIONS = [
  { value: 'single', label: 'Einzelperson' },
  { value: 'couple', label: 'Paar' },
  { value: 'family', label: 'Familie' },
  { value: 'shared', label: 'WG' },
];

const COMMUTE_OPTIONS = [
  { value: 'pt', label: 'ÖV (öffentlicher Verkehr)' },
  { value: 'car', label: 'Auto' },
  { value: 'bike', label: 'Velo' },
  { value: 'mixed', label: 'Gemischt' },
];

const WORK_PATTERN_OPTIONS = [
  { value: 'office', label: 'Büro' },
  { value: 'shift', label: 'Schichtarbeit' },
  { value: 'mixed', label: 'Hybrid / Gemischt' },
];

const PROPERTY_TYPE_OPTIONS = [
  { value: 'any', label: 'Alle' },
  { value: 'apartment', label: 'Wohnung' },
  { value: 'house', label: 'Haus' },
  { value: 'villa', label: 'Villa' },
  { value: 'attika', label: 'Attika / Penthouse' },
];

const TRANSACTION_TYPE_OPTIONS = [
  { value: 'any', label: 'Alle' },
  { value: 'buy', label: 'Kaufen' },
  { value: 'rent', label: 'Mieten' },
];

const SOFT_PRIORITY_LABELS: Record<string, string> = {
  noise: 'Ruhe / wenig Lärm',
  green: 'Grünflächen / Natur',
  pt: 'ÖV-Anbindung',
  schools: 'Nähe zu Schulen',
  tax: 'Tiefe Steuern',
  culture: 'Kultur & Gastronomie',
  oldtown: 'Altstadt / historisch',
};

function LifestyleEditTab({ profile, onSave }: { profile: any; onSave: (data: { profile: any; summary: string | null }) => void }) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Initialize form state from profile or defaults
  const [household, setHousehold] = useState(profile?.meta?.household || 'single');
  const [workPattern, setWorkPattern] = useState(profile?.meta?.work_pattern || 'office');
  const [commuteType, setCommuteType] = useState(profile?.meta?.commute_type || 'pt');
  const [childrenAges, setChildrenAges] = useState(profile?.meta?.children_ages?.join(', ') || '');

  const [maxPrice, setMaxPrice] = useState(profile?.hard?.max_price_chf?.toString() || '');
  const [minRooms, setMinRooms] = useState(profile?.hard?.min_rooms?.toString() || '');
  const [maxCommute, setMaxCommute] = useState(profile?.hard?.max_commute_minutes?.toString() || '');
  const [propertyType, setPropertyType] = useState(profile?.hard?.property_type || 'any');
  const [transactionType, setTransactionType] = useState(profile?.hard?.transaction_type || 'any');
  const [minArea, setMinArea] = useState(profile?.hard?.min_living_area_m2?.toString() || '');
  const [preferredCanton, setPreferredCanton] = useState(profile?.hard?.preferred_canton || '');
  const [preferredCity, setPreferredCity] = useState(profile?.hard?.preferred_city || '');
  const [maxBuildingAge, setMaxBuildingAge] = useState(profile?.hard?.max_building_age_years?.toString() || '');
  // Feature preferences (3-state: ja/nein/unwichtig)
  const defaultFeatures: Record<string, string> = {
    parking: 'unwichtig', balkon: 'unwichtig', garten: 'unwichtig',
    lift: 'unwichtig', minergie: 'unwichtig', neubau: 'unwichtig', rollstuhlgaengig: 'unwichtig',
    renovierungsbeduerftig: 'unwichtig', solar_interest: 'unwichtig',
  };
  // Initialize from new feature_preferences or fall back to old booleans
  const initFeatures = () => {
    const fp = profile?.hard?.feature_preferences;
    if (fp) return { ...defaultFeatures, ...fp };
    // Backward compat: map old booleans
    const mapped = { ...defaultFeatures };
    if (profile?.hard?.requires_parking) mapped.parking = 'ja';
    if (profile?.hard?.requires_balcony) mapped.balkon = 'ja';
    if (profile?.hard?.requires_garden) mapped.garten = 'ja';
    if (profile?.hard?.requires_lift) mapped.lift = 'ja';
    return mapped;
  };
  const [featurePreferences, setFeaturePreferences] = useState<Record<string, string>>(initFeatures);

  const defaultSoft: Record<string, number> = { noise: 3, green: 3, pt: 3, schools: 3, tax: 3, culture: 3, oldtown: 3 };
  const [softPriorities, setSoftPriorities] = useState<Record<string, number>>(
    profile?.soft || defaultSoft
  );

  const [workAddresses, setWorkAddresses] = useState(profile?.places?.work_addresses?.join(', ') || '');

  // Re-sync all form fields when the profile prop changes (e.g. after search auto-synthesis)
  useEffect(() => {
    if (!profile) return;
    setHousehold(profile.meta?.household || 'single');
    setWorkPattern(profile.meta?.work_pattern || 'office');
    setCommuteType(profile.meta?.commute_type || 'pt');
    setChildrenAges(profile.meta?.children_ages?.join(', ') || '');
    setMaxPrice(profile.hard?.max_price_chf?.toString() || '');
    setMinRooms(profile.hard?.min_rooms?.toString() || '');
    setMaxCommute(profile.hard?.max_commute_minutes?.toString() || '');
    setPropertyType(profile.hard?.property_type || 'any');
    setTransactionType(profile.hard?.transaction_type || 'any');
    setMinArea(profile.hard?.min_living_area_m2?.toString() || '');
    setPreferredCanton(profile.hard?.preferred_canton || '');
    setPreferredCity(profile.hard?.preferred_city || '');
    setMaxBuildingAge(profile.hard?.max_building_age_years?.toString() || '');
    // Feature preferences
    const fp = profile.hard?.feature_preferences;
    if (fp) {
      setFeaturePreferences({ ...defaultFeatures, ...fp });
    } else {
      const mapped = { ...defaultFeatures };
      if (profile.hard?.requires_parking) mapped.parking = 'ja';
      if (profile.hard?.requires_balcony) mapped.balkon = 'ja';
      if (profile.hard?.requires_garden) mapped.garten = 'ja';
      if (profile.hard?.requires_lift) mapped.lift = 'ja';
      setFeaturePreferences(mapped);
    }
    setSoftPriorities(profile.soft || defaultSoft);
    setWorkAddresses(profile.places?.work_addresses?.join(', ') || '');
  }, [profile]);

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSaving(true);

    try {
      const updatedProfile = {
        meta: {
          household,
          work_pattern: workPattern,
          commute_type: commuteType,
          children_ages: childrenAges
            ? childrenAges.split(',').map((a: string) => parseInt(a.trim(), 10)).filter((n: number) => !isNaN(n))
            : [],
        },
        hard: {
          max_price_chf: maxPrice ? parseInt(maxPrice, 10) : null,
          min_rooms: minRooms ? parseFloat(minRooms) : null,
          max_commute_minutes: maxCommute ? parseInt(maxCommute, 10) : null,
          max_commute_km: null,
          property_type: propertyType,
          transaction_type: transactionType,
          feature_preferences: featurePreferences,
          max_building_age_years: maxBuildingAge ? parseInt(maxBuildingAge, 10) : null,
          min_living_area_m2: minArea ? parseInt(minArea, 10) : null,
          preferred_canton: preferredCanton || null,
          preferred_city: preferredCity || null,
          preferred_cantons: preferredCanton ? [preferredCanton] : [],
          preferred_cities: preferredCity ? [preferredCity] : [],
        },
        soft: softPriorities,
        places: {
          work_addresses: workAddresses
            ? workAddresses.split(',').map((a: string) => a.trim()).filter(Boolean)
            : [],
          custom_pois: profile?.places?.custom_pois || [],
        },
      };

      const result = await updateLifestyleProfile(updatedProfile);
      setSuccess('Lebensstil-Profil gespeichert!');
      onSave({ profile: result.profile || updatedProfile, summary: result.summary || null });
    } catch (err: any) {
      setError(err.message || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  }

  if (!profile && !error) {
    return (
      <div className="profile-empty">
        <div className="profile-empty-icon">
          <svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
        </div>
        <h3>Kein Profil vorhanden</h3>
        <p>Führen Sie zuerst eine Immobiliensuche durch. Ihr Lebensstil-Profil wird automatisch erstellt und kann anschliessend hier bearbeitet werden.</p>
      </div>
    );
  }

  return (
    <div className="lifestyle-edit-form">
      <div className="lifestyle-edit-card">
        <h3>Lebensstil-Profil bearbeiten</h3>
        <p className="lifestyle-edit-desc">
          Bearbeiten Sie Ihre persönlichen Angaben und Prioritäten. Diese Daten werden bei künftigen Suchen berücksichtigt.
        </p>

        {error && <div className="profile-error">{error}</div>}
        {success && <div className="profile-success">{success}</div>}

        <form onSubmit={handleSave}>
          {/* Meta Section */}
          <div className="lifestyle-edit-section">
            <h4 className="lifestyle-edit-section-title">Persönliche Angaben</h4>
            <div className="lifestyle-edit-grid">
              <div className="form-group">
                <label>Haushalt</label>
                <select value={household} onChange={e => setHousehold(e.target.value)}>
                  {HOUSEHOLD_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Arbeitsmodell</label>
                <select value={workPattern} onChange={e => setWorkPattern(e.target.value)}>
                  {WORK_PATTERN_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Pendelart</label>
                <select value={commuteType} onChange={e => setCommuteType(e.target.value)}>
                  {COMMUTE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Kinder (Alter, kommagetrennt)</label>
                <input
                  type="text"
                  value={childrenAges}
                  onChange={e => setChildrenAges(e.target.value)}
                  placeholder="z.B. 5, 8, 12"
                />
              </div>
            </div>
          </div>

          {/* Hard Criteria Section */}
          <div className="lifestyle-edit-section">
            <h4 className="lifestyle-edit-section-title">Suchkriterien</h4>
            <div className="lifestyle-edit-grid">
              <div className="form-group">
                <label>Max. Budget (CHF)</label>
                <input
                  type="number"
                  value={maxPrice}
                  onChange={e => setMaxPrice(e.target.value)}
                  placeholder="z.B. 1200000"
                />
              </div>
              <div className="form-group">
                <label>Min. Zimmer</label>
                <input
                  type="number"
                  step="0.5"
                  value={minRooms}
                  onChange={e => setMinRooms(e.target.value)}
                  placeholder="z.B. 4.5"
                />
              </div>
              <div className="form-group">
                <label>Max. Pendelzeit (Min.)</label>
                <input
                  type="number"
                  value={maxCommute}
                  onChange={e => setMaxCommute(e.target.value)}
                  placeholder="z.B. 45"
                />
              </div>
              <div className="form-group">
                <label>Min. Wohnfläche (m²)</label>
                <input
                  type="number"
                  value={minArea}
                  onChange={e => setMinArea(e.target.value)}
                  placeholder="z.B. 100"
                />
              </div>
              <div className="form-group">
                <label>Objektart</label>
                <select value={propertyType} onChange={e => setPropertyType(e.target.value)}>
                  {PROPERTY_TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Kauf / Miete</label>
                <select value={transactionType} onChange={e => setTransactionType(e.target.value)}>
                  {TRANSACTION_TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Bevorzugter Kanton</label>
                <input
                  type="text"
                  value={preferredCanton}
                  onChange={e => setPreferredCanton(e.target.value)}
                  placeholder="z.B. Zürich"
                />
              </div>
              <div className="form-group">
                <label>Bevorzugte Stadt</label>
                <input
                  type="text"
                  value={preferredCity}
                  onChange={e => setPreferredCity(e.target.value)}
                  placeholder="z.B. Winterthur"
                />
              </div>
            </div>

            <h4 className="lifestyle-edit-section-title" style={{marginTop: '1.5rem'}}>Ausstattung</h4>
            <p className="lifestyle-edit-hint">Ja = zwingend erforderlich, Nein = ausgeschlossen, Unwichtig = egal</p>
            <div className="lifestyle-feature-grid">
              {[
                { key: 'parking', label: 'Parkplatz / Garage' },
                { key: 'balkon', label: 'Balkon / Terrasse' },
                { key: 'garten', label: 'Garten' },
                { key: 'lift', label: 'Lift' },
                { key: 'minergie', label: 'Minergie' },
                { key: 'neubau', label: 'Neubau' },
                { key: 'rollstuhlgaengig', label: 'Rollstuhlgängig' },
                { key: 'renovierungsbeduerftig', label: 'Renovierungsbereit' },
                { key: 'solar_interest', label: 'Solarenergie / PV-Anlage' },
              ].map(({ key, label }) => (
                <div key={key} className="lifestyle-feature-item">
                  <label>{label}</label>
                  <div className="feature-toggle-group">
                    {(['ja', 'unwichtig', 'nein'] as const).map(val => (
                      <button
                        key={val}
                        type="button"
                        className={`feature-toggle-btn feature-toggle-btn--${val} ${featurePreferences[key] === val ? 'feature-toggle-btn--active' : ''}`}
                        onClick={() => setFeaturePreferences(prev => ({ ...prev, [key]: val }))}
                      >
                        {val === 'ja' ? 'Ja' : val === 'nein' ? 'Nein' : 'Egal'}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <h4 className="lifestyle-edit-section-title" style={{marginTop: '1.5rem'}}>Gebäudealter</h4>
            <p className="lifestyle-edit-hint">Maximales Alter der Immobilie in Jahren (leer = egal)</p>
            <div className="lifestyle-edit-row">
              <div className="lifestyle-edit-field" style={{maxWidth: '260px'}}>
                <label>Max. Gebäudealter (Jahre)</label>
                <input
                  type="number"
                  min="0"
                  max="200"
                  value={maxBuildingAge}
                  onChange={e => setMaxBuildingAge(e.target.value)}
                  placeholder="z.B. 25"
                  className="lifestyle-input"
                />
              </div>
            </div>
          </div>

          {/* Soft Priorities Section */}
          <div className="lifestyle-edit-section">
            <h4 className="lifestyle-edit-section-title">Lifestyle-Prioritäten</h4>
            <p className="lifestyle-edit-hint">0 = unwichtig, 5 = sehr wichtig</p>
            <div className="lifestyle-edit-sliders">
              {Object.entries(SOFT_PRIORITY_LABELS).map(([key, label]) => (
                <div key={key} className="lifestyle-edit-slider-item">
                  <div className="lifestyle-edit-slider-header">
                    <label>{label}</label>
                    <span className="lifestyle-edit-slider-value">{softPriorities[key] || 0}</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="5"
                    step="1"
                    value={softPriorities[key] || 0}
                    onChange={e => setSoftPriorities(prev => ({ ...prev, [key]: parseInt(e.target.value, 10) }))}
                    className="lifestyle-slider"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Work Address */}
          <div className="lifestyle-edit-section">
            <h4 className="lifestyle-edit-section-title">Arbeitsort(e)</h4>
            <div className="form-group">
              <label>Arbeitsadresse(n), kommagetrennt</label>
              <input
                type="text"
                value={workAddresses}
                onChange={e => setWorkAddresses(e.target.value)}
                placeholder="z.B. Zürich HB, Winterthur"
              />
            </div>
          </div>

          <button
            type="submit"
            className="btn btn--primary btn--full"
            disabled={saving}
          >
            {saving ? 'Wird gespeichert...' : 'Profil speichern'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ============================================
// My Places Tab — "Meine besonderen Orte"
// ============================================

function MyPlacesTab({ profile, onSave }: { profile: any; onSave: (data: { profile: any; summary: string | null }) => void }) {
  const [customPois, setCustomPois] = useState<Array<{ label: string; address: string; lat: number; lng: number }>>(
    profile?.places?.custom_pois || []
  );
  const [newPoiLabel, setNewPoiLabel] = useState('');
  const [newPoiAddress, setNewPoiAddress] = useState('');
  const [poiGeocoding, setPoiGeocoding] = useState(false);
  const [poiError, setPoiError] = useState('');
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  // Re-sync when profile changes
  useEffect(() => {
    if (profile?.places?.custom_pois) {
      setCustomPois(profile.places.custom_pois);
    }
  }, [profile]);

  async function geocodeSwissAddress(address: string): Promise<{ matchedAddress: string; lat: number; lng: number } | null> {
    const url = new URL('https://api3.geo.admin.ch/rest/services/api/SearchServer');
    url.searchParams.set('type', 'locations');
    url.searchParams.set('origins', 'address');
    url.searchParams.set('searchText', address);
    url.searchParams.set('limit', '1');
    const res = await fetch(url.toString());
    if (!res.ok) return null;
    const data = await res.json();
    const first = data?.results?.[0];
    if (!first?.attrs?.lat || !first?.attrs?.lon) return null;
    const tmp = document.createElement('div');
    tmp.innerHTML = first.attrs.label || first.attrs.detail || address;
    const matchedAddress = (tmp.textContent || tmp.innerText || address).trim();
    return { matchedAddress, lat: Number(first.attrs.lat), lng: Number(first.attrs.lon) };
  }

  async function handleAddPoi() {
    if (!newPoiLabel.trim() || !newPoiAddress.trim()) {
      setPoiError('Bitte geben Sie einen Namen und eine Adresse ein.');
      return;
    }
    if (customPois.length >= 10) {
      setPoiError('Maximal 10 Orte möglich.');
      return;
    }
    setPoiError('');
    setPoiGeocoding(true);
    try {
      const result = await geocodeSwissAddress(newPoiAddress.trim());
      if (!result) {
        setPoiError('Adresse konnte nicht gefunden werden. Bitte versuchen Sie eine andere Schreibweise.');
        return;
      }
      const updatedPois = [...customPois, {
        label: newPoiLabel.trim(),
        address: result.matchedAddress,
        lat: result.lat,
        lng: result.lng,
      }];
      setCustomPois(updatedPois);
      setNewPoiLabel('');
      setNewPoiAddress('');
      // Auto-save
      await savePois(updatedPois);
    } catch {
      setPoiError('Fehler bei der Adresssuche. Bitte versuchen Sie es erneut.');
    } finally {
      setPoiGeocoding(false);
    }
  }

  async function handleRemovePoi(index: number) {
    const updatedPois = customPois.filter((_, i) => i !== index);
    setCustomPois(updatedPois);
    // Auto-save
    await savePois(updatedPois);
  }

  async function savePois(pois: Array<{ label: string; address: string; lat: number; lng: number }>) {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      // Merge with existing profile, only updating custom_pois
      const updatedProfile = {
        ...(profile || {}),
        places: {
          ...(profile?.places || {}),
          work_addresses: profile?.places?.work_addresses || [],
          custom_pois: pois,
        },
      };
      const result = await updateLifestyleProfile(updatedProfile);
      setSuccess('Orte gespeichert!');
      onSave({ profile: result.profile || updatedProfile, summary: result.summary || null });
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.message || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="lifestyle-edit-form">
      <div className="lifestyle-edit-card">
        <h3>
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="#B8860B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{verticalAlign: 'middle', marginRight: 8}}>
            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
          </svg>
          Meine besonderen Orte
        </h3>
        <p className="lifestyle-edit-desc">
          Speichern Sie persönliche Adressen (Freunde, Familie, Fitnesscenter etc.) — diese werden auf der Karte als Referenzpunkte angezeigt.
        </p>

        {error && <div className="profile-error">{error}</div>}
        {success && <div className="profile-success">{success}</div>}

        {/* Existing POIs list */}
        {customPois.length > 0 && (
          <div className="poi-list">
            {customPois.map((poi, idx) => (
              <div key={idx} className="poi-item">
                <div className="poi-item-icon">
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="#B8860B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                  </svg>
                </div>
                <div className="poi-item-info">
                  <span className="poi-item-label">{poi.label}</span>
                  <span className="poi-item-address">{poi.address}</span>
                </div>
                <button
                  type="button"
                  className="poi-item-remove"
                  onClick={() => handleRemovePoi(idx)}
                  title="Entfernen"
                  disabled={saving}
                >
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </button>
              </div>
            ))}
          </div>
        )}

        {customPois.length === 0 && (
          <div className="poi-empty">
            <svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" opacity="0.4">
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
            </svg>
            <p>Noch keine Orte gespeichert. Fügen Sie unten Ihre erste Adresse hinzu.</p>
          </div>
        )}

        {/* Add new POI form */}
        <div className="poi-add-form">
          <h4 className="poi-add-title">Neuen Ort hinzufügen</h4>
          <div className="poi-add-row">
            <input
              type="text"
              value={newPoiLabel}
              onChange={e => setNewPoiLabel(e.target.value)}
              placeholder="Name (z.B. Beste Freundin)"
              className="poi-input poi-input--label"
              maxLength={40}
            />
            <input
              type="text"
              value={newPoiAddress}
              onChange={e => setNewPoiAddress(e.target.value)}
              placeholder="Adresse (z.B. 8000 Zürich, Bahnhofstrasse 1)"
              className="poi-input poi-input--address"
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddPoi(); } }}
            />
            <button
              type="button"
              className="btn btn--secondary btn--sm poi-add-btn"
              onClick={handleAddPoi}
              disabled={poiGeocoding || saving}
            >
              {poiGeocoding ? (
                <span className="spinner-small" />
              ) : (
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              )}
            </button>
          </div>
          {poiError && <p className="poi-error">{poiError}</p>}
          <p className="poi-count">{customPois.length} / 10 Orte</p>
        </div>

        {saving && (
          <div className="poi-saving-indicator">
            <span className="spinner-small" /> Wird gespeichert...
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================
// Change Password Tab
// ============================================

// ============================================
// Valuations Tab (Pillar 2: Rate)
// ============================================

function ValuationsTab() {
  const [valuations, setValuations] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadValuations();
  }, []);

  async function loadValuations() {
    try {
      setLoading(true);
      const data = await getValuations();
      setValuations(data);
    } catch {
      setValuations([]);
    } finally {
      setLoading(false);
    }
  }

  async function handleDelete(id: number) {
    if (!confirm('Diese Analyse wirklich löschen?')) return;
    try {
      await deleteValuation(id);
      setValuations(prev => prev.filter(v => v.id !== id));
    } catch { /* ignore */ }
  }

  async function handleDeleteAll() {
    if (!confirm('Alle Analysen wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.')) return;
    try {
      await deleteAllValuations();
      setValuations([]);
    } catch { /* ignore */ }
  }

  if (loading) {
    return <div className="profile-loading"><div className="spinner" /> Analysen laden...</div>;
  }

  if (valuations.length === 0) {
    return (
      <div className="profile-empty">
        <svg viewBox="0 0 24 24" width="48" height="48" fill="none" stroke="#00CED1" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.5 }}>
          <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
        </svg>
        <p>Noch keine Analysen vorhanden.</p>
        <button className="rate-new-btn" onClick={() => navigate('/rate')}>
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg> Neue Analyse starten
        </button>
      </div>
    );
  }

  return (
    <div className="valuations-list">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h3 style={{ margin: 0, color: '#00CED1' }}>Meine Analysen ({valuations.length})</h3>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button className="rate-new-btn rate-new-btn--danger" onClick={handleDeleteAll}>
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg> Alle Analysen löschen
          </button>
          <button className="rate-new-btn" onClick={() => navigate('/rate')}>
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg> Neue Analyse
          </button>
        </div>
      </div>
      {valuations.map((v: any) => {
        const isExpanded = expandedId === v.id;
        const extracted = v.extracted || {};
        const dateStr = v.createdAt ? new Date(v.createdAt).toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';

        return (
          <div key={v.id} className="valuation-card">
            <div className="valuation-card-header" onClick={() => setExpandedId(isExpanded ? null : v.id)}>
              <div className="valuation-card-info">
                <span className="valuation-card-title">
                  {extracted.title || extracted.city || v.originalFilename || 'Bewertung'}
                </span>
                <span className="valuation-card-meta">
                  {extracted.city && `${extracted.city}${extracted.canton ? `, ${extracted.canton}` : ''}`}
                  {extracted.rooms ? ` · ${extracted.rooms} Zi.` : ''}
                  {extracted.living_space_m2 ? ` · ${extracted.living_space_m2} m²` : ''}
                  {extracted.price_chf ? ` · CHF ${Number(extracted.price_chf).toLocaleString('de-CH')}` : ''}
                </span>
                <span className="valuation-card-date">{dateStr} · {v.inputType === 'url' ? 'URL' : v.inputType === 'pdf' ? 'PDF' : 'Screenshot'}</span>
              </div>
              {v.priceEstimate?.hasData && (
                <span className={`valuation-badge valuation-badge--${v.priceEstimate.assessment}`}>
                  {v.priceEstimate.assessmentLabel}
                </span>
              )}
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ transform: isExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', flexShrink: 0, opacity: 0.5 }}><polyline points="6 9 12 15 18 9"/></svg>
            </div>

            {isExpanded && (
              <div className="valuation-card-body">
                {/* Visual Gauge + Conclusion */}
                {v.priceEstimate && (
                  <div className="valuation-verdict-section">
                    <ProfileValuationGauge
                      assessment={v.priceEstimate.assessment || 'unknown'}
                      assessmentLabel={v.priceEstimate.assessmentLabel || 'Nicht bestimmbar'}
                      deviationPercent={v.priceEstimate.deviationPercent ?? null}
                    />
                    {v.priceEstimate.shortConclusion && (
                      <p className="valuation-verdict-conclusion">{v.priceEstimate.shortConclusion}</p>
                    )}
                  </div>
                )}
                {v.valuation && (
                  <div className="valuation-text" dangerouslySetInnerHTML={{ __html: renderValuationMarkdown(v.valuation) }} />
                )}
                <div className="valuation-card-actions">
                  <button
                    className="rate-download-btn"
                    onClick={async () => {
                      const title = extracted.title || extracted.city || 'Immobilienbewertung';
                      const location = [extracted.city, extracted.canton].filter(Boolean).join(', ');
                      const pdfContainer = document.createElement('div');
                      pdfContainer.style.cssText = 'padding:24px;font-family:system-ui,-apple-system,sans-serif;color:#1e293b;font-size:13px;line-height:1.6;max-width:700px;';
                      pdfContainer.innerHTML = `
                        <style>
                          * { color: #1e293b !important; }
                          h1 { color: #00CED1 !important; }
                          h2 { font-size: 15px; margin: 18px 0 6px; border-bottom: 1px solid #e2e8f0; padding-bottom: 4px; }
                          h3 { font-size: 14px; margin: 14px 0 4px; color: #0891b2 !important; }
                          h4 { font-size: 13px; margin: 10px 0 3px; color: #0e7490 !important; }
                          h5 { font-size: 13px; margin: 8px 0 2px; color: #0891b2 !important; }
                          strong { color: #0f172a !important; }
                          table { width: 100%; border-collapse: collapse; margin: 8px 0 12px; font-size: 12px; }
                          td, th { padding: 6px 10px; border: 1px solid #cbd5e1; color: #1e293b !important; }
                          tr:first-child td { background: #f1f5f9; font-weight: 600; }
                          ul { padding-left: 18px; margin: 4px 0 8px; }
                          li { margin-bottom: 2px; }
                          hr { border: none; border-top: 1px solid #e2e8f0; margin: 12px 0; }
                          p { margin: 0 0 6px; }
                        </style>
                        <h1 style="font-size:18px;margin:0 0 4px;">SurrealEstateRate – Analyse</h1>
                        <p style="color:#64748b !important;font-size:11px;margin:0 0 16px;">${dateStr}</p>
                        <div style="margin-bottom:16px;padding:10px 14px;background:#f1f5f9;border-radius:8px;">
                          <div style="font-weight:600;margin-bottom:6px;">${title}</div>
                          ${location ? `<div style="font-size:12px;color:#64748b !important;">${location}</div>` : ''}
                          ${extracted.rooms ? `<div style="font-size:12px;color:#64748b !important;">${extracted.rooms} Zimmer · ${extracted.living_space_m2 || '?'} m² · CHF ${extracted.price_chf ? Number(extracted.price_chf).toLocaleString('de-CH') : '?'}</div>` : ''}
                          ${v.priceEstimate?.assessmentLabel ? `<div style="font-size:12px;margin-top:6px;"><strong style="color:#0891b2 !important;">Bewertung:</strong> ${v.priceEstimate.assessmentLabel}${v.priceEstimate.deviationPercent != null ? ` (${v.priceEstimate.deviationPercent > 0 ? '+' : ''}${v.priceEstimate.deviationPercent}%)` : ''}</div>` : ''}
                        </div>
                        ${v.priceEstimate?.shortConclusion ? `<div style="margin-bottom:16px;padding:10px 14px;background:#f0fdfa;border-left:3px solid #00CED1;border-radius:4px;font-style:italic;">${v.priceEstimate.shortConclusion}</div>` : ''}
                        <div>${renderValuationMarkdown(v.valuation)}</div>
                      `;
                      const html2pdf = (await import('html2pdf.js')).default;
                      html2pdf().set({
                        margin: [10, 10, 10, 10],
                        filename: `Analyse_${(title).replace(/[^a-zA-Z0-9äöüÄÖÜ]/g, '_').substring(0, 40)}_${new Date(v.createdAt).toISOString().slice(0, 10)}.pdf`,
                        image: { type: 'jpeg', quality: 0.98 },
                        html2canvas: { scale: 2, useCORS: true },
                        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
                      }).from(pdfContainer).save();
                    }}
                  >
                    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="7 10 12 15 17 10" />
                      <line x1="12" y1="15" x2="12" y2="3" />
                    </svg>
                    Als PDF herunterladen
                  </button>
                  <button className="rate-new-btn rate-new-btn--danger" onClick={() => handleDelete(v.id)}>
                    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                    Analyse löschen
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/** Visual semicircular gauge for valuation verdict (Profile page version) */
function ProfileValuationGauge({ assessment, assessmentLabel, deviationPercent }: {
  assessment: string;
  assessmentLabel: string;
  deviationPercent: number | null;
}) {
  const deviation = deviationPercent ?? 0;
  const clampedDev = Math.max(-35, Math.min(35, deviation));
  const needleAngleDeg = 90 - (clampedDev / 35) * 82;
  const needleAngleRad = (needleAngleDeg * Math.PI) / 180;

  const cx = 140, cy = 125, outerR = 95, needleR = 72;
  const needleX = cx + needleR * Math.cos(needleAngleRad);
  const needleY = cy - needleR * Math.sin(needleAngleRad);

  const verdictColor = assessment === 'below' ? '#22c55e'
    : assessment === 'above' ? '#f97316'
    : assessment === 'fair' ? '#00CED1'
    : '#94a3b8';

  function arcPath(startDeg: number, endDeg: number, r: number): string {
    const startRad = (startDeg * Math.PI) / 180;
    const endRad = (endDeg * Math.PI) / 180;
    const x1 = cx + r * Math.cos(startRad);
    const y1 = cy - r * Math.sin(startRad);
    const x2 = cx + r * Math.cos(endRad);
    const y2 = cy - r * Math.sin(endRad);
    const largeArc = Math.abs(endDeg - startDeg) > 180 ? 1 : 0;
    return `M ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 0 ${x2} ${y2}`;
  }

  return (
    <div className="rate-gauge rate-gauge--compact">
      <svg viewBox="0 0 280 155" className="rate-gauge-svg">
        <path d={arcPath(175, 120, outerR)} fill="none" stroke="#22c55e" strokeWidth="14" strokeLinecap="round" opacity="0.3" />
        <path d={arcPath(120, 60, outerR)} fill="none" stroke="#00CED1" strokeWidth="14" strokeLinecap="round" opacity="0.3" />
        <path d={arcPath(60, 5, outerR)} fill="none" stroke="#f97316" strokeWidth="14" strokeLinecap="round" opacity="0.3" />

        <text x="18" y="130" fill="#22c55e" fontSize="9" fontWeight="500" textAnchor="start" opacity="0.8">Unter</text>
        <text x="140" y="25" fill="#00CED1" fontSize="9" fontWeight="500" textAnchor="middle" opacity="0.8">Fair</text>
        <text x="262" y="130" fill="#f97316" fontSize="9" fontWeight="500" textAnchor="end" opacity="0.8">Über</text>

        <line x1={cx} y1={cy} x2={needleX} y2={needleY} stroke={verdictColor} strokeWidth="2.5" strokeLinecap="round" />
        <circle cx={needleX} cy={needleY} r={5} fill={verdictColor} />
        <circle cx={cx} cy={cy} r="6" fill="rgba(15, 23, 42, 0.9)" stroke={verdictColor} strokeWidth="2" />

        <text x={cx} y={cy + 28} fill={verdictColor} fontSize="16" fontWeight="700" textAnchor="middle">{assessmentLabel}</text>
        {deviationPercent !== null && (
          <text x={cx} y={cy + 44} fill="#94a3b8" fontSize="11" textAnchor="middle">
            {deviationPercent > 0 ? '+' : ''}{deviationPercent}% vs. Markt
          </text>
        )}
      </svg>
    </div>
  );
}

/** Simple markdown → HTML for valuation text on Profile page */
function renderValuationMarkdown(md: string): string {
  let html = md.replace(/\r\n/g, '\n');
  html = html.replace(/(?:^\|.+\|$\n?)+/gm, (block) => {
    const rows = block.trim().split('\n').filter(r => r.trim());
    const htmlRows = rows
      .filter(row => !/^[\s|\-:]+$/.test(row))
      .map(row => {
        const cells = row.split('|').filter(c => c !== '').map(c => c.trim());
        return '<tr>' + cells.map(c => `<td>${c}</td>`).join('') + '</tr>';
      })
      .join('');
    return `<table class="rate-table">${htmlRows}</table>\n`;
  });
  html = html
    .replace(/^#### (.+)$/gm, '<h5>$1</h5>')
    .replace(/^### (.+)$/gm, '<h4>$1</h4>')
    .replace(/^## (.+)$/gm, '<h3>$1</h3>')
    .replace(/^# (.+)$/gm, '<h2>$1</h2>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/(?<!\w)\*([^*]+?)\*(?!\w)/g, '<em>$1</em>')
    .replace(/^- (.+)$/gm, '<li>$1</li>')
    .replace(/((?:<li>.*<\/li>\n?)+)/g, '<ul>$1</ul>')
    .replace(/^---+$/gm, '<hr />')
    .replace(/\n{3,}/g, '\n\n')
    .replace(/\n\n/g, '</p><p>')
    .replace(/\n/g, '<br />')
    .replace(/^/, '<p>')
    .replace(/$/, '</p>');
  html = html
    .replace(/<p>\s*<\/p>/g, '')
    .replace(/<p>(<h[2345]>)/g, '$1')
    .replace(/(<\/h[2345]>)<\/p>/g, '$1')
    .replace(/<p>(<ul>)/g, '$1')
    .replace(/(<\/ul>)<\/p>/g, '$1')
    .replace(/<p>(<table)/g, '$1')
    .replace(/(<\/table>)<\/p>/g, '$1')
    .replace(/<p>(<hr \/>)/g, '$1')
    .replace(/(<hr \/>)<\/p>/g, '$1')
    .replace(/(<\/h[2345]>)<br \/>/g, '$1')
    .replace(/<br \/>(<h[2345]>)/g, '$1');
  return html;
}

// ============================================
// Newbuild Projects Tab (Pillar 3)
// ============================================

interface NbProject {
  id: number;
  name: string;
  status: string;
  municipality: string | null;
  plot_address: string | null;
  plot_zip: string | null;
  has_profile: boolean;
  has_feasibility: boolean;
  document_count: number;
  created_at: string;
  updated_at: string;
}

function NewbuildProjectsTab() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<NbProject[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const data = await apiRequest('/api/newbuild-projects');
        if (data.success) setProjects(data.data);
      } catch { /* ignore */ }
      setLoading(false);
    })();
  }, []);

  async function handleDelete(projId: number) {
    if (!confirm('Projekt wirklich löschen?')) return;
    try {
      await apiRequest(`/api/newbuild-projects/${projId}`, { method: 'DELETE' });
      setProjects(prev => prev.filter(p => p.id !== projId));
    } catch { /* ignore */ }
  }

  async function handleDeleteAll() {
    if (!confirm(`Alle ${projects.length} Projekte und deren Dokumente wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.`)) return;
    try {
      for (const proj of projects) {
        await apiRequest(`/api/newbuild-projects/${proj.id}`, { method: 'DELETE' });
      }
      setProjects([]);
    } catch { /* ignore */ }
  }

  if (loading) {
    return (
      <div className="tab-content">
        <div className="empty-state">
          <div className="spinner" /> Projekte werden geladen...
        </div>
      </div>
    );
  }

  return (
    <div className="tab-content">
      <div className="nb-projects-header">
        <h3 className="nb-projects-title">
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="#C75B4A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
          Meine Neubauprojekte
        </h3>
        <div className="nb-projects-actions">
          <a href="/newbuild" className="btn btn--sm nb-new-project-btn">
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            Neues Projekt
          </a>
          {projects.length > 0 && (
            <button className="btn btn--sm nb-delete-all-btn" onClick={handleDeleteAll}>
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
              Alle löschen
            </button>
          )}
        </div>
      </div>

      {projects.length === 0 ? (
        <div className="empty-state" style={{ padding: '2rem 0' }}>
          <svg viewBox="0 0 24 24" width="40" height="40" fill="none" stroke="#C75B4A" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.4 }}>
            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/>
          </svg>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginTop: '10px' }}>
            Noch keine Projekte. Starten Sie ein Beratungsgespräch im Neubau-Chat.
          </p>
          <a href="/newbuild" className="btn btn--primary btn--sm" style={{ marginTop: '10px', background: 'rgba(199,91,74,0.15)', border: '1px solid rgba(199,91,74,0.4)', color: '#E8856F' }}>
            Zum Neubau-Chat
          </a>
        </div>
      ) : (
        <div className="nb-projects-list">
          {projects.map(proj => (
            <div key={proj.id} className="nb-proj-card">
              <div className="nb-proj-card-top">
                <div className="nb-proj-card-info">
                  <span className="nb-proj-card-name">{proj.name}</span>
                  <span className="nb-proj-card-meta">
                    {proj.municipality || proj.plot_address || proj.plot_zip
                      ? [proj.municipality, proj.plot_address, proj.plot_zip].filter(Boolean).join(' · ')
                      : `Erstellt am ${new Date(proj.created_at).toLocaleDateString('de-CH')}`
                    }
                  </span>
                </div>
                <span className={`nb-proj-card-status nb-proj-card-status--${proj.status}`}>
                  {proj.status === 'draft' && 'Entwurf'}
                  {proj.status === 'documents_uploaded' && 'Dokumente hochgeladen'}
                  {proj.status === 'analysis_complete' && 'Analyse abgeschlossen'}
                </span>
              </div>

              <div className="nb-proj-card-stats">
                <span className="nb-proj-card-stat">
                  <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                  {proj.document_count} Dokument{proj.document_count !== 1 ? 'e' : ''}
                </span>
                {proj.has_profile && (
                  <span className="nb-proj-card-stat nb-proj-card-stat--ok">
                    <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>
                    Profil
                  </span>
                )}
                {proj.has_feasibility && (
                  <span className="nb-proj-card-stat nb-proj-card-stat--ok">
                    <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>
                    Analyse
                  </span>
                )}
              </div>

              <div className="nb-proj-card-actions">
                <button
                  className="btn btn--sm btn--primary nb-proj-action-btn"
                  onClick={() => navigate(`/newbuild/project/${proj.id}`)}
                >
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                  {proj.document_count > 0 ? 'Projekt öffnen' : 'Dokumente hinzufügen'}
                </button>
                <button
                  className="nb-proj-delete-btn"
                  onClick={() => handleDelete(proj.id)}
                  title="Projekt löschen"
                >
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================
// Newbuild Edit Tab (Pillar 3)
// ============================================

function NewbuildEditTab({ profile, onSave }: { profile: any; onSave: (updated: { profile: any; summary: string | null }) => void }) {
  const [formData, setFormData] = useState<any>(profile || {});
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);

  useEffect(() => {
    setFormData(profile || {});
  }, [profile]);

  function updateField(key: string, value: any) {
    setFormData((prev: any) => ({ ...prev, [key]: value }));
  }

  async function handleSave() {
    setSaving(true);
    setMessage(null);
    try {
      const token = getAccessToken();
      const res = await fetch('/api/newbuild-profile', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ profile: formData }),
      });
      const data = await res.json();
      if (data.success) {
        onSave({ profile: data.data.profile, summary: data.data.summary });
        setMessage({ text: 'Neubauprofil gespeichert!', type: 'success' });
      } else {
        throw new Error(data.error?.message || 'Fehler');
      }
    } catch (err: any) {
      setMessage({ text: err.message || 'Fehler beim Speichern', type: 'error' });
    }
    setSaving(false);
  }

  return (
    <div className="tab-content">
      {!profile ? (
        <div className="empty-state">
          <p style={{ color: 'var(--text-secondary)' }}>Noch kein Neubauprofil vorhanden. Führen Sie zuerst ein <a href="/newbuild" style={{ color: '#C75B4A' }}>Beratungsgespräch</a> durch.</p>
        </div>
      ) : (
        <div style={{ maxWidth: '700px' }}>
          {/* Gebäude */}
          <div className="lifestyle-edit-section">
            <h4 className="lifestyle-edit-section-title" style={{ color: '#E8856F' }}>Gebäude & Nutzung</h4>
            <div className="lifestyle-edit-grid">
              <div className="form-group">
                <label>Nutzung</label>
                <select value={formData.usage || ''} onChange={e => updateField('usage', e.target.value || null)}>
                  <option value="">–</option>
                  <option value="residential">Wohnen</option>
                  <option value="commercial">Gewerbe</option>
                  <option value="mixed">Gemischt</option>
                </select>
              </div>
              <div className="form-group">
                <label>Gebäudetyp</label>
                <select value={formData.building_type || ''} onChange={e => updateField('building_type', e.target.value || null)}>
                  <option value="">–</option>
                  <option value="detached">Einfamilienhaus</option>
                  <option value="semi_detached">Doppelhaushälfte</option>
                  <option value="apartment_building">Mehrfamilienhaus</option>
                </select>
              </div>
              <div className="form-group">
                <label>Bauweise</label>
                <select value={formData.construction_type || ''} onChange={e => updateField('construction_type', e.target.value || null)}>
                  <option value="">–</option>
                  <option value="solid">Massivbau</option>
                  <option value="wood">Holzbau</option>
                  <option value="hybrid">Hybridbau</option>
                </select>
              </div>
              <div className="form-group">
                <label>Baustil</label>
                <select value={formData.building_style || ''} onChange={e => updateField('building_style', e.target.value || null)}>
                  <option value="">–</option>
                  <option value="modern">Modern</option>
                  <option value="traditional">Traditionell</option>
                  <option value="bauhaus">Bauhaus</option>
                  <option value="other">Andere</option>
                </select>
              </div>
              <div className="form-group">
                <label>Stockwerke</label>
                <input type="number" min="1" max="10" value={formData.floors || ''} onChange={e => updateField('floors', e.target.value ? Number(e.target.value) : null)} />
              </div>
              <div className="form-group">
                <label>Dachart</label>
                <select value={formData.roof_type || ''} onChange={e => updateField('roof_type', e.target.value || null)}>
                  <option value="">–</option>
                  <option value="flat">Flachdach</option>
                  <option value="gable">Satteldach</option>
                  <option value="shed">Pultdach</option>
                  <option value="green">Begrünt</option>
                </select>
              </div>
            </div>
          </div>

          {/* Räume */}
          <div className="lifestyle-edit-section">
            <h4 className="lifestyle-edit-section-title" style={{ color: '#E8856F' }}>Räume & Fläche</h4>
            <div className="lifestyle-edit-grid">
              <div className="form-group">
                <label>Wohnfläche (m²)</label>
                <input type="number" min="50" value={formData.total_living_area_m2 || ''} onChange={e => updateField('total_living_area_m2', e.target.value ? Number(e.target.value) : null)} />
              </div>
              <div className="form-group">
                <label>Schlafzimmer</label>
                <input type="number" min="1" max="20" value={formData.bedrooms || ''} onChange={e => updateField('bedrooms', e.target.value ? Number(e.target.value) : null)} />
              </div>
              <div className="form-group">
                <label>Badezimmer</label>
                <input type="number" min="1" max="10" value={formData.bathrooms || ''} onChange={e => updateField('bathrooms', e.target.value ? Number(e.target.value) : null)} />
              </div>
            </div>
            <div className="nb-edit-checkboxes">
              {[
                { key: 'office', label: 'Home-Office' },
                { key: 'hobby_room', label: 'Hobbyraum' },
                { key: 'fitness_room', label: 'Fitnessraum' },
                { key: 'large_windows', label: 'Grosse Fenster' },
                { key: 'basement', label: 'Keller' },
                { key: 'granny_flat', label: 'Einliegerwohnung' },
              ].map(({ key, label }) => (
                <label key={key} className="nb-edit-checkbox">
                  <input type="checkbox" checked={!!formData[key]} onChange={e => updateField(key, e.target.checked)} /> {label}
                </label>
              ))}
            </div>
          </div>

          {/* Aussen & Energie */}
          <div className="lifestyle-edit-section">
            <h4 className="lifestyle-edit-section-title" style={{ color: '#E8856F' }}>Aussen & Energie</h4>
            <div className="nb-edit-checkboxes">
              {[
                { key: 'terrace', label: 'Terrasse' },
                { key: 'balcony', label: 'Balkon' },
                { key: 'pergola', label: 'Pergola' },
                { key: 'garden', label: 'Garten' },
                { key: 'pool', label: 'Pool' },
                { key: 'photovoltaic', label: 'Photovoltaik' },
                { key: 'smart_home', label: 'Smart Home' },
                { key: 'accessibility', label: 'Barrierefrei' },
                { key: 'elevator', label: 'Lift' },
                { key: 'ev_charging', label: 'E-Ladestation' },
              ].map(({ key, label }) => (
                <label key={key} className="nb-edit-checkbox">
                  <input type="checkbox" checked={!!formData[key]} onChange={e => updateField(key, e.target.checked)} /> {label}
                </label>
              ))}
            </div>
            <div className="lifestyle-edit-grid" style={{ marginTop: '1rem' }}>
              <div className="form-group">
                <label>Heizung</label>
                <select value={formData.heating_type || ''} onChange={e => updateField('heating_type', e.target.value || null)}>
                  <option value="">–</option>
                  <option value="heat_pump_air">Luft-Wärmepumpe</option>
                  <option value="heat_pump_geothermal">Erdwärme</option>
                  <option value="district_heating">Fernwärme</option>
                  <option value="pellets">Pellets</option>
                  <option value="other">Andere</option>
                </select>
              </div>
              <div className="form-group">
                <label>Minergie</label>
                <select value={formData.minergie_standard || ''} onChange={e => updateField('minergie_standard', e.target.value || null)}>
                  <option value="">–</option>
                  <option value="none">Kein Minergie</option>
                  <option value="minergie">Minergie</option>
                  <option value="minergie_p">Minergie-P</option>
                  <option value="minergie_a">Minergie-A</option>
                </select>
              </div>
              <div className="form-group">
                <label>Parkplätze</label>
                <input type="number" min="0" max="10" value={formData.parking_spaces || ''} onChange={e => updateField('parking_spaces', e.target.value ? Number(e.target.value) : null)} />
              </div>
            </div>
          </div>

          {/* Grundstück & Budget */}
          <div className="lifestyle-edit-section">
            <h4 className="lifestyle-edit-section-title" style={{ color: '#E8856F' }}>Grundstück & Budget</h4>
            <div className="lifestyle-edit-grid">
              <div className="form-group">
                <label>Gemeinde</label>
                <input type="text" value={formData.plot_municipality || ''} onChange={e => updateField('plot_municipality', e.target.value || null)} />
              </div>
              <div className="form-group">
                <label>Grundstücksfläche (m²)</label>
                <input type="number" value={formData.plot_size_m2 || ''} onChange={e => updateField('plot_size_m2', e.target.value ? Number(e.target.value) : null)} />
              </div>
              <div className="form-group">
                <label>Budget (CHF)</label>
                <input type="number" value={formData.budget_chf || ''} onChange={e => updateField('budget_chf', e.target.value ? Number(e.target.value) : null)} />
              </div>
              <div className="form-group">
                <label>Zeitrahmen</label>
                <input type="text" value={formData.timeline || ''} onChange={e => updateField('timeline', e.target.value || null)} placeholder="z.B. Frühling 2027" />
              </div>
            </div>
          </div>

          {/* Besondere Wünsche */}
          <div className="lifestyle-edit-section">
            <h4 className="lifestyle-edit-section-title" style={{ color: '#E8856F' }}>Besondere Wünsche</h4>
            <div className="form-group">
              <textarea
                value={formData.special_wishes || ''}
                onChange={e => updateField('special_wishes', e.target.value || null)}
                placeholder="Besondere Wünsche oder Anmerkungen..."
                style={{ minHeight: '80px', resize: 'vertical' }}
              />
            </div>
          </div>

          {message && (
            <div className={`lifestyle-message lifestyle-message--${message.type}`} style={{ marginBottom: '12px' }}>
              {message.text}
            </div>
          )}

          <button
            className="btn btn--primary btn--full"
            style={{ background: 'rgba(199,91,74,0.2)', borderColor: 'rgba(199,91,74,0.5)', color: '#E8856F' }}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Wird gespeichert...' : 'Neubauprofil speichern'}
          </button>
        </div>
      )}
    </div>
  );
}
